import PropTypes from 'prop-types';

const recommendationDate = (value) => {
  if (!value) return '';
  const date = value?.toDate?.() || new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

export const RecommendationList = ({
  title,
  iconClass,
  items,
  type,
  onAdd,
  onDiscard,
  showHeading = true,
}) => (
  <section>
    {showHeading ? (
      <h3 className="h5 mb-3">
        <i className={`bi ${iconClass} me-2`} aria-hidden="true"></i>
        {title}
      </h3>
    ) : null}
    {items.length ? (
      <div className="row g-3">
        {items.map((item, index) => (
          <div className="col-12 col-lg-6" key={`${item.title || item.name}-${index}`}>
            <article className="card h-100 border-secondary-subtle shadow-sm">
              <div className="card-body">
                <h4 className="h6 mb-2">{item.title || item.name}</h4>
                <p className="text-muted small mb-2">{item.overview || item.description}</p>
                {item.why ? (
                  <p className="small mb-0"><strong>Why this helps:</strong> {item.why}</p>
                ) : null}
                {type === 'project' && Array.isArray(item.steps) && item.steps.length ? (
                  <div className="projects-skills-summary-plan mt-3">
                    <strong className="small">Suggested plan</strong>
                    <ol className="mb-0 mt-2">
                      {item.steps.map((step, stepIndex) => (
                        <li key={`${step}-${stepIndex}`}>{step}</li>
                      ))}
                    </ol>
                  </div>
                ) : null}
                {type === 'project' && item.outcome ? (
                  <p className="projects-skills-summary-outcome small mb-0 mt-3">
                    <i className="bi bi-flag me-2" aria-hidden="true"></i>
                    <strong>Finished when:</strong> {item.outcome}
                  </p>
                ) : null}
                <div className="d-flex flex-wrap gap-2 mt-3">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    type="button"
                    onClick={() => onAdd(item)}
                  >
                    <i className="bi bi-plus-lg me-2" aria-hidden="true"></i>
                    Add {type}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    type="button"
                    onClick={() => onDiscard(item)}
                  >
                    <i className="bi bi-x-lg me-2" aria-hidden="true"></i>
                    Discard
                  </button>
                </div>
              </div>
            </article>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-muted small mb-0">No {type} recommendations yet.</p>
    )}
  </section>
);

export const CollapsibleRecommendationList = ({ title, iconClass, items, type, onAdd, onDiscard }) => (
  <details className="projects-skills-collapsible-recommendations">
    <summary>
      <span className="projects-skills-collapsible-title">
        <i className={`bi ${iconClass}`} aria-hidden="true"></i>
        <strong>{title}</strong>
      </span>
      <span className="projects-skills-collapsible-count">
        {items.length} recommendation{items.length === 1 ? '' : 's'}
      </span>
      <i className="bi bi-chevron-down projects-skills-collapsible-chevron" aria-hidden="true"></i>
    </summary>
    <div className="projects-skills-collapsible-content">
      <RecommendationList
        title={title}
        iconClass={iconClass}
        items={items}
        type={type}
        onAdd={onAdd}
        onDiscard={onDiscard}
        showHeading={false}
      />
    </div>
  </details>
);

const ProjectsSkillsSummary = ({
  projectCount,
  skillCount,
  jobCount,
  loading = false,
  recommendations,
  onAddProject,
  onAddSkill,
  onDiscardProject,
  onDiscardSkill,
}) => {
  const projectRecommendations = Array.isArray(recommendations?.projects)
    ? recommendations.projects
    : [];
  const skillRecommendations = Array.isArray(recommendations?.skills)
    ? recommendations.skills
    : [];
  const updatedAt = recommendationDate(recommendations?.updatedAt);

  return (
    <div className="projects-skills-summary px-3 px-md-0" role="tabpanel" aria-label="Summary">
      <div className="alert alert-light border mb-4">
        Desired jobs tell the AI what to emphasize, skills describe your abilities, and projects provide evidence of what you have built. Together they form the context used to generate your portfolio.
      </div>
      <div className="row g-3 mb-4" aria-label="Portfolio content totals">
        <div className="col-12 col-sm-4">
          <div className="projects-skills-summary-stat">
            <i className="bi bi-briefcase" aria-hidden="true"></i>
            <span>{loading ? <span className="spinner-border spinner-border-sm" aria-hidden="true"></span> : jobCount}</span>
            <small>Desired Jobs</small>
          </div>
        </div>
        <div className="col-12 col-sm-4">
          <div className="projects-skills-summary-stat">
            <i className="bi bi-award" aria-hidden="true"></i>
            <span>{loading ? <span className="spinner-border spinner-border-sm" aria-hidden="true"></span> : skillCount}</span>
            <small>Skills</small>
          </div>
        </div>
        <div className="col-12 col-sm-4">
          <div className="projects-skills-summary-stat">
            <i className="bi bi-kanban" aria-hidden="true"></i>
            <span>{loading ? <span className="spinner-border spinner-border-sm" aria-hidden="true"></span> : projectCount}</span>
            <small>Projects</small>
          </div>
        </div>
      </div>

      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h2 className="h4 mb-1">AI recommendations</h2>
          <p className="text-muted mb-0">Ideas based on your saved work, skills, and target jobs.</p>
        </div>
        {updatedAt ? <small className="text-muted text-nowrap">Updated {updatedAt}</small> : null}
      </div>

      {!projectRecommendations.length && !skillRecommendations.length ? (
        <div className="alert alert-info mb-0">
          Use the AI button to generate recommendations for your next project or skill.
        </div>
      ) : (
        <div className="d-grid gap-4">
          <RecommendationList
            title="Project ideas"
            iconClass="bi-kanban"
            items={projectRecommendations}
            type="project"
            onAdd={onAddProject}
            onDiscard={onDiscardProject}
          />
          <RecommendationList
            title="Skill ideas"
            iconClass="bi-award"
            items={skillRecommendations}
            type="skill"
            onAdd={onAddSkill}
            onDiscard={onDiscardSkill}
          />
        </div>
      )}
    </div>
  );
};

RecommendationList.propTypes = {
  title: PropTypes.string.isRequired,
  iconClass: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  type: PropTypes.string.isRequired,
  onAdd: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
  showHeading: PropTypes.bool,
};

CollapsibleRecommendationList.propTypes = {
  title: PropTypes.string.isRequired,
  iconClass: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  type: PropTypes.string.isRequired,
  onAdd: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
};

ProjectsSkillsSummary.propTypes = {
  projectCount: PropTypes.number.isRequired,
  skillCount: PropTypes.number.isRequired,
  jobCount: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  recommendations: PropTypes.object,
  onAddProject: PropTypes.func.isRequired,
  onAddSkill: PropTypes.func.isRequired,
  onDiscardProject: PropTypes.func.isRequired,
  onDiscardSkill: PropTypes.func.isRequired,
};

export default ProjectsSkillsSummary;
